import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, ChevronDown, ExternalLink, Heart } from "lucide-react";
import { toast } from "sonner";
import { SIDE_SUBMENU } from "../../data/extra";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useAuth } from "../../contexts/AuthContext";
import { Logo } from "../Logo";
import ExclusiveIcon from "./ExclusiveIcon";
import { safeFetch } from "../../lib/safeFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches per-primary-category sub-categories (providers) from the backend
 * once per session. Falls back to the hardcoded SIDE_SUBMENU object so the
 * drawer keeps working if the API request fails. Returns a dict keyed by
 * category slug, where each value is `[{ id, label }, ...]`.
 */
const useSubCategories = () => {
  const [subs, setSubs] = useState(null); // null = loading
  useEffect(() => {
    let cancelled = false;
    safeFetch(`${BACKEND_URL}/api/public/sub-categories`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setSubs(data || {}); })
      .catch(() => { if (!cancelled) setSubs({}); });
    return () => { cancelled = true; };
  }, []);
  return subs;
};

const MenuItem = ({ m, onClose, subsByCategory }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const slug = m.slug || m.id;
  // Sub-categories only apply to Casino & Slots — every other primary
  // category is a single flat link to its landing page.
  const SUB_ENABLED = new Set(["casino", "slots"]);
  // Prefer dynamic sub-categories from DB (providers in admin's catalog).
  // Fall back to the hardcoded SIDE_SUBMENU only if the API call hasn't
  // surfaced any rows for this slug — keeps the drawer useful before the
  // admin has seeded any games for that section.
  const dbSub = subsByCategory && subsByCategory[slug];
  const sub = SUB_ENABLED.has(slug)
    ? (dbSub && dbSub.length > 0 ? dbSub : SIDE_SUBMENU[slug])
    : null;
  const active = location.pathname === m.to || location.pathname.startsWith(m.to + "/");

  // Renders the row icon: special-cases the Favourite slug to a yellow heart
  // (the category has no CDN image asset), otherwise uses the icon URL from
  // the API/fallback. Always passes through the Exclusive special icon too.
  const renderIcon = () => {
    if (slug === "exclusive") return <ExclusiveIcon size={26} active={active} />;
    if (slug === "favourite") {
      return (
        <Heart
          size={24}
          fill="#c89b3c"
          stroke="#c89b3c"
          strokeWidth={1.5}
        />
      );
    }
    if (m.icon) {
      return (
        <img
          src={m.icon}
          alt=""
          className="h-6 w-6 opacity-90"
          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        />
      );
    }
    return null;
  };

  // Favourite category requires login. Guests get a toast + redirect.
  const handleFavGuardedClick = (e) => {
    if (slug === "favourite" && !user) {
      e.preventDefault();
      toast.info("Please log in to view your favourites");
      onClose && onClose();
      navigate("/login");
    }
  };

  if (!sub) {
    return (
      <Link
        to={m.to}
        onClick={(e) => { handleFavGuardedClick(e); if (!e.defaultPrevented) onClose && onClose(); }}
        data-testid={`side-menu-${slug}`}
        className={`flex items-center gap-3 px-5 py-3 hover:bg-white/5 ${
          active ? "text-white border-l-2 border-[#19a55c] bg-white/5" : "text-white/85"
        }`}
      >
        {renderIcon()}
        <span
          className="font-medium"
          style={slug === "favourite" ? { color: "#c89b3c" } : undefined}
        >
          {m.label}
        </span>
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid={`side-menu-${slug}-toggle`}
        className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-white/5 ${
          active ? "text-white" : "text-white/85"
        }`}
      >
        {renderIcon()}
        <span className="font-medium flex-1 text-left">{m.label}</span>
        <ChevronDown
          size={16}
          className={`text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="bg-black/30 py-1">
          <Link
            to={m.to}
            onClick={onClose}
            data-testid={`side-submenu-${slug}-all`}
            className="flex items-center gap-3 pl-14 pr-5 py-2 text-sm text-[#19a55c] hover:bg-white/5"
          >
            All {m.label}
          </Link>
          {sub.map((s) => (
            <button
              key={s.id}
              data-testid={`side-submenu-${slug}-${s.id}`}
              onClick={() => {
                navigate(`${m.to}?provider=${encodeURIComponent(s.label)}`);
                onClose();
              }}
              className="w-full text-left flex items-center gap-3 pl-14 pr-5 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SideMenu = ({ open, onClose }) => {
  const { primary, secondary } = useSiteCategories();
  const subsByCategory = useSubCategories();
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        data-testid="side-menu"
        className={`fixed top-0 left-0 z-50 h-screen w-[300px] bg-[#0e1620] border-r border-white/5 transition-transform overflow-y-auto ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-white/5 sticky top-0 bg-[#0e1620] z-10">
          <Link to="/" onClick={onClose} className="flex items-center" data-testid="side-menu-logo-link">
            <Logo className="text-xl" />
          </Link>
          <button
            onClick={onClose}
            data-testid="side-menu-close"
            className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-white/5"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="py-3">
          {primary.map((m) => (
            <MenuItem key={m.slug || m.id} m={m} onClose={onClose} subsByCategory={subsByCategory} />
          ))}
        </nav>

        <div className="border-t border-white/5 my-2" />

        <nav className="py-2">
          {secondary.map((m) => (
            <Link
              key={m.slug || m.id}
              to={m.to}
              onClick={onClose}
              data-testid={`side-menu-${m.slug || m.id}`}
              className="flex items-center justify-between px-5 py-3 hover:bg-white/5 text-white/75 hover:text-white text-sm"
            >
              {m.label}
              {(m.slug === "affiliate" || m.id === "affiliate") && <ExternalLink size={14} className="text-white/40" />}
            </Link>
          ))}
        </nav>

        <div className="px-5 py-4 text-xs text-white/40 border-t border-white/5">
          © 2026 Maxlive88 — Play Smart, Win Big
        </div>
      </aside>
    </>
  );
};
